<template>
  <v-app v-if="!langLoading" :class="{ 'xx-shell': hasShell }">
    <a v-if="hasShell" class="shell-skip" href="#shell-main" @click.prevent="skipToContent">
      {{ shellText('SHELL.skipToContent') }}
    </a>

    <!-- I5618: design system shell on every screen that has the sidebar -->
    <header v-if="hasShell" class="shell-bar">
      <div class="shell-logo-row">
        <button ref="menuToggle" class="shell-bar-btn shell-menu-toggle" type="button"
          :aria-label="shellText('SHELL.menu')" :aria-expanded="sidebarOpen ? 'true' : 'false'"
          aria-controls="shell-sidebar" @click="toggleSidebar">
          <span class="ms" aria-hidden="true">menu</span>
        </button>
        <router-link class="shell-home" to="/" @click.native="closeSidebar">
          <span class="shell-mark" aria-hidden="true">XX</span>
          <span class="shell-app-name">Customer dashboard</span>
        </router-link>
      </div>
      <div class="shell-bar-right">
        <label class="shell-lang">
          <span class="ms" aria-hidden="true">language</span>
          <span class="shell-sr-only">{{ shellText('SHELL.language') }}</span>
          <select @change="changeLanguage">
            <option v-for="lang in languageOptions" :key="lang" :value="lang" :selected="lang === selectedLang">
              {{ String(lang).toUpperCase() }}
            </option>
          </select>
          <span class="ms shell-lang-chevron" aria-hidden="true">expand_more</span>
        </label>
        <component :is="isGLNSwitchAvailable ? 'button' : 'div'" v-if="clientInfo" ref="companyButton"
          class="shell-company" :class="{ 'shell-company--switch': isGLNSwitchAvailable }"
          v-bind="companyAreaAttrs" v-on="companyAreaListeners">
          <span class="shell-avatar" aria-hidden="true">{{ companyInitials }}</span>
          <span class="shell-company-info">
            <span class="shell-company-name">{{ clientInfo.companyName }}</span>
            <span class="shell-company-gln">{{ clientInfo.informationProvider }}</span>
          </span>
          <span v-if="isGLNSwitchAvailable" class="ms shell-company-chevron" aria-hidden="true">expand_more</span>
        </component>
      </div>
      <div v-if="showCompanySearch" class="shell-search" @keydown.esc.capture.stop="closeCompanySearch">
        <v-autocomplete :loading="loading" v-model="selectedClient" :items="clientsList" autofocus
          item-text="companyName" item-value="companyName" outlined dense hide-details return-object
          color="var(--primary-text)" :aria-label="shellText('SHELL.searchCompany')"
          :placeholder="shellText('SHELL.searchCompany')" :no-data-text="companySearchEmptyText"
          :menu-props="{ contentClass: 'shell-search-menu' }" @blur="closeGLNSearch"
          @update:search-input="updateGLN">
          <template v-slot:item="data">
            <div class="shell-search-item">
              <span class="shell-search-item-name">{{ data.item.companyName }}</span>
              <span class="shell-search-item-gln">{{ data.item.informationProvider }}</span>
            </div>
          </template>
        </v-autocomplete>
      </div>
    </header>

    <!-- Login and GLN selection keep the white bar and language menu; an unresolved route has no bar -->
    <v-app-bar v-else-if="currentRouteName" app color="white" elevate-on-scroll>
      <v-container class="d-flex align-center py-0">
        <v-spacer></v-spacer>
        <div style="position: relative;" class="d-flex align-center " v-if="clientInfo && !showGLNSearch" @click="() => {
          if (isGLNSwitchAvailable) {
            showGLNSearch = true;
          }
        }">
          <div class="circleCompany">
            <span style="margin: auto; padding-top: 2px;">{{ clientInfo.companyName?.substring(0, 2) }}</span>
          </div>
          <p class="d-flex flex-column text-end mb-0 mr-12">
            <span class="user-name">{{ clientInfo.companyName }}</span>
            <span class="user-position">{{ clientInfo.informationProvider }}</span>
          </p>
          <v-icon
            style="right: 0; position: absolute; height: 32px; width: 56px; color: #9E9E9E;">mdi-menu-down</v-icon>
        </div>
        <div class="d-flex align-center" v-if="isGLNSwitchAvailable && showGLNSearch">
          <v-autocomplete rounded class="searchGLN" :loading="loading" v-model="selectedClient" :items="clientsList"
            autofocus="true" item-text="companyName" item-value="companyName" @blur="closeGLNSearch"
            @update:search-input="updateGLN" return-object>
            <template v-slot:item="data">
              <div class="client-list-item py-2">
                <span class="font-weight-light">{{ data.item.companyName }}</span>
                <br />
                <span class="primary--text font-weight-medium">{{ data.item.informationProvider }}</span>
              </div>
            </template>
          </v-autocomplete>

        </div>
        <v-menu left bottom offset-y>
          <template v-slot:activator="{ on: menuOn }">
            <v-btn v-on="menuOn" fab text color="grey">
              {{ selectedLang }}
              <v-icon style="right: 0; position: absolute; ">{{ menuOn ? 'mdi-menu-down' : 'mdi-menu-up' }}</v-icon>
            </v-btn>
          </template>

          <v-list>
            <v-list-item v-for="lang in languages" :key="lang">
              <v-list-item-title @click="updateLang(lang)" class="grey--text text-uppercase" style="cursor: pointer">{{
                lang }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-container>
    </v-app-bar>

    <div class="app-body">
      <div v-if="hasShell && sidebarOpen" class="shell-backdrop" @click="closeSidebar"></div>
      <aside v-if="hasShell" id="shell-sidebar" ref="sidebar" class="shell-side"
        :class="{ 'shell-side--open': sidebarOpen }" :aria-label="shellText('SHELL.mainMenu')">
        <nav class="shell-nav">
          <!-- I5618: one item; the tabs on the page lead to open and completed registrations -->
          <router-link v-slot="{ href, navigate }" :to="{ name: 'dashboard' }" custom>
            <a :href="href" class="shell-item" :class="{ 'shell-item--active': ordersItemCurrent }"
              :aria-current="ordersItemCurrent" @click="navigate($event); closeSidebar()">
              <span class="ms" aria-hidden="true">shopping_cart</span>
              <span class="shell-label">{{ $t('GENERAL.orders') }}</span>
            </a>
          </router-link>
        </nav>
        <div v-if="isGLNSwitchAvailable" class="shell-foot">
          <!-- I5618: the way to start an upload, on every page; pinned at the bottom, above the version link -->
          <button class="shell-new-registration" type="button"
            :aria-label="shellText('SHELL.newRegistration')" :title="newRegistrationTitle"
            :disabled="isUploadItemLocked" @click="openUploadGLNModal">
            <span class="ms" aria-hidden="true">add</span>
            <span class="shell-new-registration-label">{{ shellText('SHELL.newRegistration') }}</span>
          </button>
          <button class="shell-version" type="button" @click="openVersionModal">
            <span class="ms" aria-hidden="true">new_releases</span>
            {{ versionLinkLabel }}
          </button>
        </div>
      </aside>

      <v-main id="shell-main" key="main" class="pb-8" :tabindex="hasShell ? -1 : null"
        :inert="sidebarOpen ? 'inert' : null">
        <router-view />
      </v-main>
    </div>

    <!-- "Wat is nieuw": the entries stay in Dutch, as in versions.md -->
    <v-dialog ref="versionDialog" v-model="showVersionModal" max-width="560" content-class="shell-modal"
      overlay-color="var(--modal-backdrop)" overlay-opacity="1">
      <div class="shell-modal-head">
        <span class="ms" aria-hidden="true">new_releases</span>
        <h2 id="shell-version-title" class="shell-modal-title">{{ shellText('SHELL.whatsNew') }}</h2>
        <button class="shell-modal-close" type="button" :aria-label="shellText('SHELL.close')"
          @click="showVersionModal = false">
          <span class="ms" aria-hidden="true">close</span>
        </button>
      </div>
      <div class="shell-modal-body">
        <section v-for="(entry, entryIndex) in versionLog" :key="`${entry.version}-${entryIndex}`"
          class="shell-log-version">
          <h3 class="shell-log-title">Versie {{ entry.version }}</h3>
          <p class="shell-log-date">{{ versionDate(entry.date) }}</p>
          <div v-for="(item, index) in entry.items" :key="index" class="shell-log-item">
            <h4 v-if="item.section" class="shell-log-section">{{ item.section }}</h4>
            <p v-if="item.title" class="shell-log-item-title">{{ item.title }}</p>
            <ul v-if="item.lines.length">
              <li v-for="(line, lineIndex) in item.lines" :key="lineIndex"
                :class="{ 'shell-log-line--nested': line.nested }">{{ line.text }}</li>
            </ul>
          </div>
        </section>
        <div v-if="!versionLog.length" class="shell-empty">
          <span class="ms shell-empty-icon" aria-hidden="true">inbox</span>
          <p class="shell-empty-title">{{ shellText('SHELL.noVersions') }}</p>
        </div>
      </div>
    </v-dialog>

    <!-- GLN Selection Modal for UPLOAD FILES (I5618) -->
    <v-dialog v-model="showUploadGLNModal" max-width="500px">
      <company-selector
        :show-cancel="false"
        :initial-company="clientInfo"
        @company-selected="handleUploadGLNSelected"
      />
    </v-dialog>
  </v-app>
</template>

<script>
import CompanyService from '@/api/company/companyService.js';
import { formBus } from '@/main.js';
import { loadLanguageAsync, loadLanguagesListAsync, setI18nLanguage } from './i18n.js';
import { useTitle } from '@vueuse/core';
import apiInst from './api/api.js';
import { mdiVectorArrangeBelow, mdiBullhorn } from '@mdi/js'
import CompanySelector from '@/components/CompanySelector.vue'
import { parseVersionLog, formatVersionDate } from '@/utils/versionLog.js'

// I5618: routes without the sidebar. A route that has no name yet counts as no shell as well.
const ROUTES_WITHOUT_SHELL = ['login', 'gln'];
// I5618: the routes the one menu item stands for. It links to the dashboard, so only there is it the
// current page itself; the other tab and the upload page (in its breadcrumb) sit under it.
const ORDERS_ITEM_CURRENT = { dashboard: 'page', orderHistory: 'true', uploadFiles: 'true' };

// I5618: shell texts [nl, en]; a translation row with the same key overrides these fallbacks.
const SHELL_TEXTS = {
  'SHELL.skipToContent': ['Naar de inhoud', 'Skip to content'],
  'SHELL.menu': ['Menu', 'Menu'],
  'SHELL.mainMenu': ['Hoofdmenu', 'Main menu'],
  'SHELL.language': ['Taal', 'Language'],
  'SHELL.switchCompany': ['Bedrijf wisselen', 'Switch company'],
  'SHELL.searchCompany': ['Zoek een bedrijf', 'Search for a company'],
  'SHELL.noCompanyFound': ['Geen bedrijf gevonden', 'No company found'],
  'SHELL.companiesFailed': [
    'De bedrijvenlijst kon niet worden geladen. Probeer het later opnieuw.',
    'The company list could not be loaded. Try again later.',
  ],
  'SHELL.versionLink': ['Versie {version} — wat is nieuw', "Version {version} — what's new"],
  'SHELL.whatsNew': ['Wat is nieuw', "What's new"],
  'SHELL.noVersions': ['Er zijn nog geen versies om te tonen', 'There are no versions to show yet'],
  'SHELL.close': ['Sluiten', 'Close'],
  'SHELL.newRegistration': ['Nieuwe aanmelding', 'New registration'],
  'SHELL.newRegistrationLocked': [
    'Niet beschikbaar: er loopt een upload of er zijn bestanden gekozen.',
    'Not available: an upload is in progress or files have been chosen.',
  ],
};

export default {
  name: 'App',
  components: {
    CompanySelector
  },
  data: () => ({
    path: mdiVectorArrangeBelow,
    megaphonePath: mdiBullhorn,
    openedMenu: false,
    showGLNSearch: false,
    showUploadGLNModal: false,
    showVersionModal: false,
    sidebarOpen: false,
    uploadState: null,
    versionLog: [],
    versionLogRequested: false,
    loading: false,
    clientsList: null,
    selectedClient: null,
    clientsListError: null,
    langLoading: true,
    languages: [],
    clientInfo: JSON.parse(localStorage.getItem('client')),
    isGLNSwitchAvailable: JSON.parse(localStorage.getItem('generalCompany')) == 'XXtract'
  }),

  computed: {
    currentRouteName() {
      return this.$route.name;
    },
    ordersItemCurrent() {
      return ORDERS_ITEM_CURRENT[this.currentRouteName] || null;
    },
    hasShell() {
      return Boolean(this.currentRouteName) && !ROUTES_WITHOUT_SHELL.includes(this.currentRouteName);
    },
    // A switch would lose the chosen files or leave the bar on another company than the batch.
    isCompanySwitchLocked() {
      const state = this.uploadState;
      return Boolean(state) && (state.stage !== 'choose' || state.hasFiles);
    },
    isUploadItemLocked() {
      return this.isCompanySwitchLocked && this.uploadState.stage !== 'outcome';
    },
    newRegistrationTitle() {
      return this.shellText(this.isUploadItemLocked ? 'SHELL.newRegistrationLocked' : 'SHELL.newRegistration');
    },
    showCompanySearch() {
      return Boolean(this.clientInfo) && this.isGLNSwitchAvailable && this.showGLNSearch && !this.isCompanySwitchLocked;
    },
    companyInitials() {
      return String((this.clientInfo && this.clientInfo.companyName) || '').substring(0, 2);
    },
    companyTitle() {
      if (!this.clientInfo) return '';
      return [this.clientInfo.companyName, this.clientInfo.informationProvider].filter(Boolean).join(' · ');
    },
    companyAreaAttrs() {
      if (!this.isGLNSwitchAvailable) {
        return { title: this.companyTitle, role: 'img', 'aria-label': this.companyTitle };
      }
      return {
        type: 'button',
        title: this.companyTitle,
        disabled: this.isCompanySwitchLocked,
        'aria-label': `${this.shellText('SHELL.switchCompany')}: ${this.companyTitle}`,
        'aria-expanded': this.showCompanySearch ? 'true' : 'false',
      };
    },
    companyAreaListeners() {
      if (!this.isGLNSwitchAvailable) return {};
      // No mousedown focus: it would blur the search field first and reopen the panel on click.
      return { mousedown: (event) => event.preventDefault(), click: this.toggleCompanySearch };
    },
    companySearchEmptyText() {
      return this.shellText(this.clientsListError ? 'SHELL.companiesFailed' : 'SHELL.noCompanyFound');
    },
    languageOptions() {
      const selected = this.selectedLang;
      return !selected || this.languages.includes(selected) ? this.languages : [selected, ...this.languages];
    },
    currentVersion() {
      return this.versionLog.length ? this.versionLog[0].version : null;
    },
    versionLinkLabel() {
      return this.currentVersion
        ? this.shellText('SHELL.versionLink', { version: this.currentVersion })
        : this.shellText('SHELL.whatsNew');
    },
    selectedLang() {
      return this.$store.getters['language/getLanguage'];
    },
    isLoggedIn() {
      return this.clientInfo !== null;
    },
    mediaUrl() {
      return process.env.VUE_APP_MEDIA_URL;
    },
    version() {
      return process.env.LAST_COMMIT_SHA;
    },
  },

  watch: {
    // Every way of closing the overlay hands focus back; next tick, after a closing dialog restored its own.
    sidebarOpen(open) {
      if (open) return;
      this.$nextTick(() => {
        if (this.$refs.menuToggle) this.$refs.menuToggle.focus();
      });
    },
    $route(next, prev) {
      this.sidebarOpen = false;
      if (prev.name === 'gln' && next.name === 'dashboard') {
        this.loadZohoChat();
        this.getClientsList();
        this.getTooltips();
      }
      if (prev.name === 'login' && next.name === 'dashboard') {
        this.loadZohoChat();
        this.getTooltips();
      }
    }
  },

  async beforeCreate() {
    try {
      await loadLanguageAsync();
    } catch (e) {
      console.log('LANG', e);
    } finally {
      this.langLoading = false;
    }
  },

  created() {
    if (this.isLoggedIn) {
      this.loadZohoChat();
      this.getTooltips();
    }
    this.getClientsList();
    this.setLanguagesList();
    this.loadVersionLog();
    formBus.$on('clientAdd', this.setClientInfo);
    formBus.$on('uploadStage', this.setUploadStage);
    document.addEventListener('wheel', this.handleScroll);
    document.addEventListener('keydown', this.handleShellKeydown);
    window.addEventListener('resize', this.handleShellResize);
    const lang = localStorage.getItem('language') || 'en';
    this.updateLang(lang);
    setI18nLanguage(lang);

  },

  beforeDestroy() {
    formBus.$off('clientAdd', this.setClientInfo);
    formBus.$off('uploadStage', this.setUploadStage);
    document.removeEventListener('keydown', this.handleShellKeydown);
    window.removeEventListener('resize', this.handleShellResize);
  },

  methods: {
    // I5618: the translation key may not exist yet; then the inline text per language, with {name} placeholders.
    shellText(key, params = {}) {
      const [nl, en] = SHELL_TEXTS[key];
      const fallback = (this.$i18n && this.$i18n.locale) === 'nl' ? nl : en;
      const translated = this.$te(key) ? this.$t(key) : '';
      const text = String(translated).trim() ? translated : fallback;
      return String(text).replace(/\{(\w+)\}/g, (match, name) => (name in params ? String(params[name]) : match));
    },
    versionDate(date) {
      return formatVersionDate(date);
    },
    skipToContent() {
      const main = document.getElementById('shell-main');
      if (main) main.focus();
    },
    toggleSidebar() {
      this.sidebarOpen = !this.sidebarOpen;
      if (!this.sidebarOpen) return;
      this.$nextTick(() => {
        const first = this.$refs.sidebar && this.$refs.sidebar.querySelector('a, button');
        if (first) first.focus();
      });
    },
    closeSidebar() {
      this.sidebarOpen = false;
    },
    handleShellKeydown(event) {
      if (event.key !== 'Escape' || !this.sidebarOpen) return;
      // A dialog opened from the overlay closes on its own first; by now its flag may already be off.
      if (this.showVersionModal || this.showUploadGLNModal) return;
      if (event.target && event.target.closest && event.target.closest('.v-dialog__content')) return;
      this.closeSidebar();
    },
    handleShellResize() {
      if (window.innerWidth >= 1280) this.closeSidebar();
    },
    // I5618: its own chunk, loaded for XXtract users only; the older entries are technical.
    async loadVersionLog() {
      if (!this.isGLNSwitchAvailable || this.versionLogRequested) return;
      this.versionLogRequested = true;
      try {
        const file = await import(/* webpackChunkName: "version-log" */ 'raw-loader!../versions.md');
        this.versionLog = parseVersionLog(file.default);
      } catch (e) {
        console.log('VERSIONS', e);
      }
    },
    async changeLanguage(event) {
      const select = event.target;
      try {
        await this.updateLang(select.value);
      } catch (e) {
        console.log('LANG', e);
      }
      select.value = this.selectedLang;
    },
    toggleCompanySearch() {
      this.showGLNSearch = !this.showGLNSearch;
    },
    closeCompanySearch() {
      this.showGLNSearch = false;
      this.$nextTick(() => {
        const button = this.$refs.companyButton;
        if (button && button.focus) button.focus();
      });
    },
    openVersionModal() {
      this.showVersionModal = true;
      // Vuetify puts role="dialog" on an inner element and takes no attributes for it.
      this.$nextTick(() => {
        const dialog = this.$refs.versionDialog && this.$refs.versionDialog.$refs.content;
        if (dialog) dialog.setAttribute('aria-labelledby', 'shell-version-title');
      });
    },
    setUploadStage(state) {
      this.uploadState = state;
      if (this.isCompanySwitchLocked) this.showGLNSearch = false;
    },
    // I5618: "Nieuwe aanmelding" keeps the company choice, then opens the single upload page.
    openUploadGLNModal() {
      this.showUploadGLNModal = true;
    },
    handleUploadGLNSelected(selectedCompany) {
      localStorage.setItem('client', JSON.stringify(selectedCompany));
      this.clientInfo = selectedCompany;
      formBus.$emit('clientAdd');
      this.showUploadGLNModal = false;
      this.sidebarOpen = false;
      if (this.currentRouteName !== 'uploadFiles') {
        this.$router.push({ name: 'uploadFiles' });
      }
    },
    getTooltips() {
      apiInst.get('/tooltips').then((response) => {
        const tooltips = response.data.reduce((acc, item) => {
          const { language, entryInstructionsCustomer, toolTipField } = item;
          if (!acc[language]) {
            acc[language] = {};
          }
          if (entryInstructionsCustomer) {
            acc[language][toolTipField] = entryInstructionsCustomer;
          }
          return acc;
        }, {});
        this.$store.commit('language/SET_TOOLTIPS', tooltips);
      });
    },
    closeGLNSearch() {
      this.showGLNSearch = false;
    },
    async getClientsList() {
      try {
        this.loading = true;
        const res = await CompanyService.getClientsList();
        this.clientsList = res.data;
        this.clientsListError = null;
      } catch (error) {
        console.log('Cant get clients list');
        if (!error.response) {
          this.clientsListError = {
            error: 'Failed',
            message: 'Connection timeout'
          };
        } else {
          this.clientsListError = error.response.data;
        }
      } finally {
        this.loading = false;
      }
    },
    async setLanguagesList() {
      const list = await loadLanguagesListAsync();
      this.languages = list.data;
      await loadLanguageAsync();
      useTitle(this.$t('GENERAL.CustomerDashboard'));
    },
    setClientInfo() {
      this.isGLNSwitchAvailable = JSON.parse(localStorage.getItem('generalCompany')) == 'XXtract';
      this.clientInfo = JSON.parse(localStorage.getItem('client'));
      this.loadVersionLog();
    },
    updateGLN() {
      if (this.selectedClient) {
        localStorage.setItem('client', JSON.stringify(this.selectedClient));
        formBus.$emit('clientAdd');
        this.showGLNSearch = false;
        window.location.reload();
      }
    },
    async updateLang(lang) {
      await loadLanguageAsync(lang);
      this.$store.dispatch('language/updateLanguage', lang);
      useTitle(this.$t('GENERAL.CustomerDashboard'));
    },
    loadZohoChat() {
      var scripts = [
        "zohochat"
      ];
      scripts.forEach(script => {
        let tag = document.createElement("script");
        if (script.includes("zohochat")) {
          if (document.getElementById("zsiqchat") === null) {
            tag.setAttribute("id", "zsiqchat");
            tag.innerHTML = 'var $zoho=$zoho || {};$zoho.salesiq = $zoho.salesiq || {widgetcode: "00f33205e195e32084969fefc7d2a574c93d49f8bce31ceeb8f8f427eac76f22", values:{},ready:function(){}};var d=document;s=d.createElement("script");s.type="text/javascript";s.id="zsiqscript";s.defer=true;s.src="https://salesiq.zoho.eu/widget";t=d.getElementsByTagName("script")[0];t.parentNode.insertBefore(s,t);';
          } else {
            console.log("Zoho Chat already loaded")
          }
        } else {
          tag.setAttribute("src", script);
        }
        document.body.appendChild(tag);
      });
    },
  }
};
</script>

<style lang="scss">
.v-menu__content.theme--light.v-menu__content--fixed.menuable__content__active {
  border-radius: 0 !important;

}

.dropdownList {
  padding: 0;
  border: 1px solid #858585 !important;
  border-radius: 0 !important;
  overflow: hidden;
}

.addProductLink {
  width: 100%;
  display: flex;
  text-decoration: none;
  color: #858585 !important;

  >.v-list-item.theme--light {
    color: unset !important;
  }

  &:hover {
    color: black !important;
    background-color: #f5f5f5 !important;
  }
}

.theme--light.v-list-item {
  &:hover {
    color: black !important;
  }
}

.theme--light.v-list-item--active::before {
  opacity: 0 !important;
}

.circleCompany {
  height: 40px;
  width: 40px;
  background-color: #53959f;
  border-radius: 50%;
  display: inline-flex;
  color: white;
  margin-right: 16px;
}

.no-active:before {
  background-color: unset !important;
}

.searchGLN {
  width: 300px;
  margin-right: 16px;

  .v-text-field__details {
    display: none !important;
  }

  .v-input__slot {
    margin-bottom: 0px;
    padding: 0 !important;
  }
}

.no-decoration {
  text-decoration: none !important;
}

.vertical-divider {
  height: 50px;
  top: 0;
  bottom: 0;
  margin: auto 0;
  border-left: 1px solid lightgrey;
}

.v-btn--active.no-active::before {
  opacity: 0 !important;
}

$font-family: '-apple-system';

.v-application {
  [class*='text-'] {
    font-family: $font-family, BlinkMacSystemFont, sans-serif !important;
  }

  font-family: $font-family,
  BlinkMacSystemFont,
  sans-serif !important;
}

.user-name {
  font-size: 12px;
  letter-spacing: 0;
}

.user-position {
  font-size: 12px;
  letter-spacing: 0;
  color: #53959f;
}

.user-avatar {
  border: 4px solid #e9ebf4;
  transition: all ease 0.5s;

  &:hover {
    border: 0px solid transparent;
  }
}

.zsiq_floatmain {
  margin-bottom: 120px;
  margin-right: 32px;
}

/* I5618: portal shell in the design system. All colours come from tokens.scss. */
@import './styles/scss/tokens.scss';

$shell-font: 'Inter', system-ui, sans-serif;

/* Wraps sidebar and page on every route; only with a shell does the sidebar take part. */
.app-body {
  display: flex;
  flex: 1 0 auto;
  width: 100%;
  min-width: 0;

  >.v-main {
    flex: 1 1 0;
    min-width: 0;
  }
}

/* Detached tooltips, menus and dialogs sit outside this wrapper, so they always open above the bar. */
.xx-shell>.v-application--wrap {
  isolation: isolate;
}

#shell-main:focus {
  outline: 0;
}

.ms {
  display: inline-block;
  flex: none;
  width: 1em;
  height: 1em;
  overflow: hidden;
  font-family: 'Material Symbols Outlined', sans-serif;
  font-size: var(--t-icon);
  font-style: normal;
  font-weight: 400;
  font-variation-settings: 'FILL' 0, 'wght' 400;
  letter-spacing: normal;
  line-height: 1;
  text-transform: none;
  white-space: nowrap;
  user-select: none;
}

.shell-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.xx-shell a.shell-skip {
  position: fixed;
  top: -4rem;
  left: 8px;
  z-index: calc(var(--z-topbar) + 1);
  padding: 8px 16px;
  border-radius: 8px;
  background: var(--background);
  box-shadow: var(--card-shadow-md);
  color: var(--foreground);
  font-family: $shell-font;
  font-size: var(--t-sm);
  text-decoration: none;

  &:focus {
    top: 8px;
    outline: 2px solid var(--secondary-text);
    outline-offset: 2px;
  }
}

/* Top bar: the one navy surface */
.shell-bar {
  position: sticky;
  top: 0;
  z-index: var(--z-topbar);
  display: flex;
  flex: none;
  align-items: center;
  gap: 12px;
  height: var(--topbar-h);
  padding: 0 16px;
  background: var(--primary);
  box-shadow: var(--topbar-shadow);
  color: var(--nav-text);
  font-family: $shell-font;

  :focus-visible {
    outline: 2px solid var(--on-primary);
    outline-offset: 2px;
  }

  .shell-search :focus-visible {
    outline: 0;
  }
}

.shell-logo-row {
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.shell-bar-btn {
  display: inline-grid;
  flex: none;
  place-items: center;
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--nav-text-dim);
  cursor: pointer;
  transition: all 0.1s;

  &:hover {
    background: var(--nav-hover);
  }
}

.xx-shell a.shell-home {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  border-radius: 7px;
  text-decoration: none;
}

.shell-mark {
  display: grid;
  flex: none;
  place-items: center;
  width: 28px;
  height: 28px;
  border-radius: 7px;
  background: var(--success);
  color: var(--on-success);
  font-size: var(--t-xs);
  font-weight: 700;
}

.shell-app-name {
  overflow: hidden;
  color: var(--on-primary);
  font-size: var(--t-logo);
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.shell-bar-right {
  display: flex;
  flex: none;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

/* I5618: the upload entry point in the sidebar foot: design system primary button, full width */
.xx-shell .shell-new-registration {
  display: flex;
  box-sizing: border-box;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  min-height: 44px;
  margin-bottom: 8px;
  padding: 8px 16px;
  border: 1px solid var(--primary);
  border-radius: 8px;
  background: var(--primary);
  color: var(--on-primary);
  cursor: pointer;
  font-family: inherit;
  font-size: var(--t-sm);
  font-weight: 500;
  line-height: 1.5;
  text-align: center;
  transition: all 0.1s;

  .ms {
    font-size: 0.9375rem;
  }

  &:hover:not(:disabled) {
    border-color: var(--primary-hover);
    background: var(--primary-hover);
  }

  &:focus-visible {
    outline: 2px solid var(--secondary-text);
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
}

.shell-lang {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  color: var(--nav-text-dim);

  select {
    min-height: 28px;
    padding: 4px 20px 4px 4px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: var(--nav-text);
    cursor: pointer;
    font-family: inherit;
    font-size: var(--t-xs);
    font-weight: 500;
  }

  option {
    background: var(--background);
    color: var(--foreground);
  }
}

.shell-lang-chevron {
  position: absolute;
  right: 0;
  pointer-events: none;
}

.shell-company {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  padding: 4px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--nav-text-dim);
  font-family: inherit;
  text-align: left;
}

.shell-company--switch {
  cursor: pointer;
  transition: all 0.1s;

  &:hover:not(:disabled) {
    background: var(--nav-hover);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
}

.shell-avatar {
  display: grid;
  flex: none;
  place-items: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: var(--primary-light);
  color: var(--primary-text);
  font-size: var(--t-tiny);
  font-weight: 600;
  text-transform: uppercase;
}

/* From 768px name and GLN beside the avatar; below that the avatar alone */
.shell-company-info,
.shell-company-chevron {
  display: none;
}

.shell-company-name,
.shell-company-gln {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.shell-company-name {
  color: var(--on-primary);
  font-size: var(--t-sm);
  font-weight: 500;
}

.shell-company-gln {
  color: var(--nav-text-dim);
  font-size: var(--t-micro);
}

.shell-search {
  position: absolute;
  top: calc(100% + 4px);
  right: 16px;
  width: #{"min(300px, calc(100vw - 32px))"};
  padding: 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--background);
  box-shadow: var(--card-shadow-md);

  .v-input {
    font-size: var(--t-sm);
  }
}

.v-menu__content.shell-search-menu.shell-search-menu {
  border-radius: 8px !important;
  /* As wide as the search field: long company names wrap instead of widening the page */
  max-width: #{"min(274px, calc(100vw - 58px))"};
  font-family: $shell-font;

  .theme--light.v-list-item:hover {
    color: var(--foreground) !important;
  }
}

.shell-search-item {
  display: flex;
  flex-direction: column;
  padding: 8px 0;
}

.shell-search-item-name {
  color: var(--foreground);
  overflow-wrap: anywhere;
  font-size: var(--t-sm);
}

.shell-search-item-gln {
  color: var(--foreground-muted);
  font-size: var(--t-xs);
}

/* Sidebar: an overlay below the bar under 1280px, sticky from 1280px */
.shell-side {
  position: fixed;
  top: var(--topbar-h);
  bottom: 0;
  left: 0;
  z-index: var(--z-sidebar-overlay);
  display: flex;
  flex-direction: column;
  width: var(--sidebar-w);
  border-right: 1px solid var(--border);
  background: var(--background);
  font-family: $shell-font;
  transform: translateX(-100%);
  transition: transform 0.2s ease;
  visibility: hidden;

  :focus-visible {
    outline: 2px solid var(--secondary-text);
    outline-offset: -2px;
  }
}

.shell-side--open {
  transform: none;
  visibility: visible;
}

.shell-backdrop {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: var(--z-sidebar-backdrop);
  background: var(--sidebar-backdrop);
}

.shell-nav {
  flex: 1 1 auto;
  min-height: 0;
  padding: 12px 0;
  overflow-y: auto;
}

.xx-shell .shell-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: calc(100% - 16px);
  min-height: 44px;
  margin: 1px 8px;
  padding: 6px 10px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--foreground-muted);
  cursor: pointer;
  font-family: inherit;
  font-size: var(--t-sm);
  font-weight: 500;
  text-align: left;
  text-decoration: none;
  /* Not 'all': visibility would lag behind the overlay and block moving focus into it */
  transition: background-color 0.1s, color 0.1s;

  &:hover {
    background: var(--background-muted);
  }
}

.xx-shell .shell-item--active,
.xx-shell .shell-item--active:hover {
  background: var(--primary-light);
  color: var(--primary-text);
  font-weight: 600;
}

.shell-label::first-letter {
  text-transform: uppercase;
}

.shell-foot {
  flex: none;
  padding: 12px 16px;
  border-top: 1px solid var(--border);
}

.shell-version {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-height: 28px;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: none;
  color: var(--foreground-muted);
  cursor: pointer;
  font-family: inherit;
  font-size: var(--t-tiny);
  text-align: left;

  &:hover {
    color: var(--foreground);
  }
}

/* Design system modal */
.v-dialog.shell-modal.shell-modal {
  display: flex;
  flex-direction: column;
  max-height: 80vh;
  overflow: hidden;
  border-radius: 14px;
  background: var(--background);
  box-shadow: var(--card-shadow-md);
  color: var(--foreground);
  font-family: $shell-font;
  font-size: var(--t-sm);

  :focus-visible {
    outline: 2px solid var(--secondary-text);
    outline-offset: 2px;
  }

  ul {
    margin: 4px 0 0;
    padding-left: 18px;
  }

  li {
    margin-top: 4px;
  }
}

.shell-modal-head {
  display: flex;
  flex: none;
  align-items: center;
  gap: 8px;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border-soft);

  >.ms {
    color: var(--foreground-muted);
  }
}

.shell-modal-title {
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  color: var(--foreground-heading);
  font-size: var(--t-logo);
  font-weight: 700;
}

.shell-modal-close {
  display: inline-grid;
  flex: none;
  place-items: center;
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--foreground-muted);
  cursor: pointer;
  transition: all 0.1s;

  &:hover {
    background: var(--background-muted);
    color: var(--foreground);
  }
}

.shell-modal-body {
  flex: 1 1 auto;
  min-height: 0;
  padding: 20px;
  overflow-y: auto;
}

.shell-log-version+.shell-log-version {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid var(--border-soft);
}

.shell-log-title {
  margin: 0;
  color: var(--foreground-heading);
  font-size: var(--t-sm);
  font-weight: 600;
}

.shell-log-date {
  margin: 0;
  color: var(--foreground-muted);
  font-size: var(--t-xs);
}

.shell-log-item {
  margin-top: 12px;
}

.shell-log-section {
  margin: 0 0 4px;
  color: var(--foreground-muted);
  font-size: var(--t-micro);
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.shell-log-item-title {
  margin: 0;
  font-weight: 600;
}

.shell-log-line--nested {
  margin-left: 18px;
}

.shell-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 20px;
  text-align: center;
}

.shell-empty-icon {
  color: var(--foreground-muted);
  font-size: 2.5rem;
  opacity: 0.5;
}

.shell-empty-title {
  margin: 12px 0 0;
  font-size: var(--t-body);
  font-weight: 500;
}

@media (pointer: coarse) {
  .shell-bar-btn,
  .shell-company--switch,
  .shell-version,
  .shell-modal-close,
  .shell-lang select {
    min-width: 44px;
    min-height: 44px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .shell-side {
    transition: none;
  }
}

@media (min-width: 768px) {
  .shell-logo-row {
    flex: none;
    width: calc(var(--sidebar-w) - 16px);
  }

  /* The bar's only flexible part from here on: a long company name truncates instead of pushing the page wider */
  .shell-bar-right {
    flex: 0 1 auto;
    min-width: 0;
  }

  .shell-company-info {
    display: block;
    min-width: 0;
    max-width: 13rem;
    line-height: 1.25;
  }

  .shell-company-chevron {
    display: inline-block;
  }
}

@media (min-width: 1280px) {
  .shell-menu-toggle,
  .shell-backdrop {
    display: none;
  }

  .shell-side {
    position: sticky;
    top: var(--topbar-h);
    bottom: auto;
    flex: none;
    align-self: flex-start;
    height: calc(100vh - var(--topbar-h));
    transform: none;
    transition: none;
    visibility: visible;
  }
}

@import './styles/scss/variables.scss';
@import './styles/scss/mixins.scss';
@import './styles/scss/main.scss';
</style>